/**
 * 枚举与中文显示名映射 — 与《数据库设计 v2.0》§1 一致：
 * 库里存 UPPER_SNAKE_CASE 明文字符串，中文显示名在前端映射（禁 int 编码）。
 */

export type Tone = 'ok' | 'warn' | 'dan' | 'pri' | 'mut' | 'vio';

export interface EnumMeta<T extends string> {
  label: string;
  tone?: Tone;
  /** 枚举码本身（UPPER_SNAKE_CASE），便于埋点与选项透传 */
  code?: T;
}

export const USER_TYPE = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  OPS_ADMIN: 'OPS_ADMIN',
  DATA_ANALYST: 'DATA_ANALYST',
  DISPATCH_SUPERVISOR: 'DISPATCH_SUPERVISOR',
  BPO_AGENT: 'BPO_AGENT',
  COURIER: 'COURIER',
} as const;
export type UserType = (typeof USER_TYPE)[keyof typeof USER_TYPE];

export const USER_TYPE_META: Record<UserType, EnumMeta<UserType>> = {
  SUPER_ADMIN: { label: '超级管理员' },
  OPS_ADMIN: { label: '运营管理员' },
  DATA_ANALYST: { label: '数据分析师' },
  // t77：显示名统一为「交付机构管理员」（role code DISPATCH_SUPERVISOR 冻结不变）
  DISPATCH_SUPERVISOR: { label: '交付机构管理员' },
  BPO_AGENT: { label: 'BPO客服', tone: 'warn' },
  COURIER: { label: '交付专员', tone: 'pri' },
};

export const USER_STATUS = {
  ACTIVE: 'ACTIVE',
  PENDING_AUDIT: 'PENDING_AUDIT',
  DISABLED: 'DISABLED',
  SUSPENDED: 'SUSPENDED',
} as const;
export type UserStatus = (typeof USER_STATUS)[keyof typeof USER_STATUS];

export const USER_STATUS_META: Record<UserStatus, EnumMeta<UserStatus>> = {
  ACTIVE: { label: '正常', tone: 'ok' },
  PENDING_AUDIT: { label: '待审核', tone: 'warn' },
  DISABLED: { label: '禁用', tone: 'dan' },
  SUSPENDED: { label: '停用', tone: 'mut' },
};

export const USER_END = { ADMIN: 'ADMIN', BPO: 'BPO', MINIAPP: 'MINIAPP' } as const;
export type UserEnd = (typeof USER_END)[keyof typeof USER_END];

export const USER_END_META: Record<UserEnd, EnumMeta<UserEnd>> = {
  ADMIN: { label: '管理端', tone: 'pri' },
  BPO: { label: '录单端', tone: 'warn' },
  MINIAPP: { label: '小程序', tone: 'vio' },
};

/** 端绑定互斥：外部角色仅绑专属端，禁止开通管理端 */
export const EXTERNAL_ENDS: readonly UserEnd[] = [USER_END.BPO, USER_END.MINIAPP];

export const COURIER_LEVEL = { GOLD: 'GOLD', SILVER: 'SILVER', BRONZE: 'BRONZE' } as const;
export type CourierLevel = (typeof COURIER_LEVEL)[keyof typeof COURIER_LEVEL];

export const COURIER_LEVEL_META: Record<CourierLevel, EnumMeta<CourierLevel>> = {
  GOLD: { label: '金牌', tone: 'warn' },
  SILVER: { label: '银牌', tone: 'pri' },
  BRONZE: { label: '铜牌', tone: 'mut' },
};

export const COURIER_DUTY = { ONLINE: 'ONLINE', OFFLINE: 'OFFLINE', ON_LEAVE: 'ON_LEAVE' } as const;
export type CourierDuty = (typeof COURIER_DUTY)[keyof typeof COURIER_DUTY];

export const COURIER_DUTY_META: Record<CourierDuty, EnumMeta<CourierDuty>> = {
  ONLINE: { label: '接单中', tone: 'ok' },
  OFFLINE: { label: '已下线', tone: 'mut' },
  ON_LEAVE: { label: '请假', tone: 'warn' },
};

export const CERT_STATUS = { CERTIFIED: 'CERTIFIED', PENDING: 'PENDING', MISSING: 'MISSING' } as const;
export type CertStatus = (typeof CERT_STATUS)[keyof typeof CERT_STATUS];

export const CERT_STATUS_META: Record<CertStatus, EnumMeta<CertStatus>> = {
  CERTIFIED: { label: '已认证', tone: 'ok' },
  PENDING: { label: '认证中', tone: 'warn' },
  MISSING: { label: '缺失', tone: 'dan' },
};

export const DATA_SCOPE = {
  ALL: 'ALL',
  OWN_REGION: 'OWN_REGION',
  SELF_ONLY: 'SELF_ONLY',
  SELF_ENTERED_ONLY: 'SELF_ENTERED_ONLY',
} as const;
export type DataScope = (typeof DATA_SCOPE)[keyof typeof DATA_SCOPE];

export const DATA_SCOPE_META: Record<DataScope, EnumMeta<DataScope>> = {
  ALL: { label: '全部' },
  OWN_REGION: { label: '本区域（所辖）' },
  SELF_ONLY: { label: '仅本人' },
  SELF_ENTERED_ONLY: { label: '仅本人录入' },
};

/** 订单状态（VARCHAR+CHECK，可扩展）。管理端页签按业务分组聚合多状态。 */
export const ORDER_STATUS = {
  PENDING_ASSIGN: 'PENDING_ASSIGN',
  POOL_PENDING: 'POOL_PENDING',
  ASSIGNED: 'ASSIGNED',
  ACCEPTED: 'ACCEPTED',
  DELIVERING: 'DELIVERING',
  DELIVERED: 'DELIVERED',
  AWAITING_CALLBACK: 'AWAITING_CALLBACK',
  CARRIER_CONFIRMED: 'CARRIER_CONFIRMED',
  COMPLETED: 'COMPLETED',
  EXCEPTION: 'EXCEPTION',
  CANCELLED: 'CANCELLED',
  /* 第 13 码（紧急需求20261029 需求1）：后端 OrderStatus.CARD_FAILED，与交付提交页第五档「开卡失败」同源。
     用户拍板是**终态**（不可改派重办）。本端改前完全没见过这枚码 ⇒ 交付端提交后 BPO 侧只能显示原码，
     需求原文要求三端状态展示同步。 */
  CARD_FAILED: 'CARD_FAILED',
} as const;
export type OrderStatus = (typeof ORDER_STATUS)[keyof typeof ORDER_STATUS];

/**
 * 展示层归并表（反馈文档_09 第 4 条 A 方案）：待回调 ⇒ 交付完成。
 * 只描述「怎么显示」，⛔ 不参与任何写面/状态机——码值、后端 OrderStatus、历史单与结算 BillStatus 一律不动。
 */
export const ORDER_STATUS_DISPLAY_MERGED: Partial<Record<OrderStatus, OrderStatus>> = {
  [ORDER_STATUS.AWAITING_CALLBACK]: ORDER_STATUS.DELIVERED,
};

/** 展示层不再单列的码（归并表键集；状态下拉与阶段都按归并后的口径铺） */
export const ORDER_STATUS_DISPLAY_MERGED_CODES: readonly OrderStatus[] = [
  ORDER_STATUS.AWAITING_CALLBACK,
];

/** 展示用归并：命中归并表走目标码，其余原样返回；未知码也原样返回（⛔ 抛错、⛔ 吞成空串）。 */
export function displayOrderStatusOf(status: OrderStatus | string): OrderStatus | string {
  return ORDER_STATUS_DISPLAY_MERGED[status as OrderStatus] ?? status;
}

export const ORDER_STATUS_META: Record<OrderStatus, EnumMeta<OrderStatus>> = {
  PENDING_ASSIGN: { label: '待分配', tone: 'dan' },
  POOL_PENDING: { label: '公区待领', tone: 'warn' },
  ASSIGNED: { label: '已分配', tone: 'pri' },
  ACCEPTED: { label: '已接单', tone: 'pri' },
  DELIVERING: { label: '交付中', tone: 'pri' },
  DELIVERED: { label: '交付完成', tone: 'ok' },
  /* 反馈文档_09 第 4 条（A 方案：后端状态机与历史数据一律不动，只收敛展示层）：交付完成与待回调在业务上属同一阶段，
     两个状态重复 ⇒ 本端不再把它当一枚独立状态展示：标签并入「交付完成」、状态下拉不再单列
     （ORDER_STATUS_FILTER_OPTIONS）。码值仍在册：列表行里历史单仍可能回这一枚码，labelOf 一律渲染成「交付完成」。 */
  AWAITING_CALLBACK: { label: '交付完成', tone: 'ok' },
  CARRIER_CONFIRMED: { label: '运营商确认', tone: 'ok' },
  COMPLETED: { label: '已完结', tone: 'ok' },
  EXCEPTION: { label: '异常', tone: 'dan' },
  CANCELLED: { label: '已取消', tone: 'mut' },
  /* 失败族同色调：开卡失败不是进行中态，是「卡没开成」的业务失败结果。 */
  CARD_FAILED: { label: '开卡失败', tone: 'dan' },
};

/**
 * 状态下拉选项的**唯一**来源：已并入交付完成的那枚不再单列（否则下拉里会出现两枚同名「交付完成」）。
 * 未知码防线在 labelOf：它回落原码，故这里只负责枚数与文案，不去猜码。
 */
export const ORDER_STATUS_FILTER_OPTIONS: ReadonlyArray<{ value: OrderStatus; label: string }> =
  (Object.keys(ORDER_STATUS_META) as OrderStatus[])
    .filter((code) => !ORDER_STATUS_DISPLAY_MERGED_CODES.includes(code))
    .map((code) => ({ value: code, label: ORDER_STATUS_META[code].label }));

export const ORDER_TYPE = { NEW_CARD: 'NEW_CARD', RENEW: 'RENEW', UPGRADE: 'UPGRADE' } as const;
export type OrderType = (typeof ORDER_TYPE)[keyof typeof ORDER_TYPE];

/** order_type 取值待 PRD 补全（当前以 V4.9 UI + 后端契约为准） */
export const ORDER_TYPE_META: Record<OrderType, EnumMeta<OrderType>> = {
  NEW_CARD: { label: '新办卡' },
  RENEW: { label: '续约' },
  UPGRADE: { label: '升级套餐' },
};

export const DELIVERY_METHOD = {
  ON_SITE: 'ON_SITE',
  EXPRESS: 'EXPRESS',
  SELF_PICKUP: 'SELF_PICKUP',
} as const;
export type DeliveryMethod = (typeof DELIVERY_METHOD)[keyof typeof DELIVERY_METHOD];

/** 冻结取值：后端文档 v1.5 §6.0-⑧（三值保留，明文字符串传输，不出现 int） */
export const DELIVERY_METHOD_META: Record<DeliveryMethod, EnumMeta<DeliveryMethod>> = {
  ON_SITE: { label: '上门交付' },
  EXPRESS: { label: '快递寄送' },
  SELF_PICKUP: { label: '自提' },
};

/**
 * §6.0-⑧：P0 只承诺 ON_SITE（上门）。EXPRESS / SELF_PICKUP 归 P1 —— 六步交付 SOP 与现场
 * 凭证规则均按上门场景设计，另两种缺 SOP 支撑。P0 表单须置灰/隐藏，但字段仍可提交存储。
 */
export const DELIVERY_METHOD_P0: readonly DeliveryMethod[] = [DELIVERY_METHOD.ON_SITE];

export function isDeliveryMethodP0(method: DeliveryMethod): boolean {
  return DELIVERY_METHOD_P0.includes(method);
}

/**
 * 订单中心状态页签（V4.9 六页签 + 全部，聚合 §5 的 11 个码值）。
 * 页签为固定定义，计数来自 §6.2-1 `GET /orders/status-counts` 的码值映射求和，
 * 因此码值缺失按 0 计，页签数量不随数据抖动。
 */
export interface OrderStatusTab {
  key: string;
  label: string;
  statuses: OrderStatus[];
}

export const ORDER_STATUS_TABS: readonly OrderStatusTab[] = [
  { key: 'all', label: '全部', statuses: [] },
  { key: 'wait', label: '待分配', statuses: [ORDER_STATUS.PENDING_ASSIGN] },
  { key: 'pool', label: '公区待领', statuses: [ORDER_STATUS.POOL_PENDING] },
  { key: 'doing', label: '交付中', statuses: [ORDER_STATUS.ASSIGNED, ORDER_STATUS.ACCEPTED, ORDER_STATUS.DELIVERING] },
  { key: 'carrier', label: '运营商确认', statuses: [ORDER_STATUS.DELIVERED, ORDER_STATUS.AWAITING_CALLBACK, ORDER_STATUS.CARRIER_CONFIRMED] },
  { key: 'end', label: '已完结', statuses: [ORDER_STATUS.COMPLETED] },
  { key: 'abnormal', label: '异常/取消', statuses: [ORDER_STATUS.EXCEPTION, ORDER_STATUS.CANCELLED] },
  /* 开卡失败单列一格（紧急需求20261029 需求1）：⛔ 挂进「异常/取消」——那是交付过程出错／用户撤销，
     与「客户资料到了、卡开不出来」是两种业务结果；且同一枚码进两枚页签会让计数之和 ≠ 总数。 */
  { key: 'cardfailed', label: '开卡失败', statuses: [ORDER_STATUS.CARD_FAILED] },
];

/**
 * 运营商（反馈_11：全端共用一套，四值）。
 *
 * BPO 端历史上自己维护过一枚三值字典（TELECOM/UNICOM/MOBILE），与超管/交付端的运营商
 * 枚举不是一套：那枚没有广电、码形也不同，两边各写各的中文名。这批统一并入本枚，
 * BPO 端不再另存一份。⛔ 别把本枚折叠回三值（广电产品无处可落，后端 Enum.IsDefined 直接 422）；
 * ⛔ 视图也⛔ 另写中文名，标签只许取下面这张表。
 */
export const PRODUCT_CARRIER = {
  CHINA_TELECOM: 'CHINA_TELECOM',
  CHINA_MOBILE: 'CHINA_MOBILE',
  CHINA_UNICOM: 'CHINA_UNICOM',
  CHINA_BROADNET: 'CHINA_BROADNET',
} as const;
export type ProductCarrier = (typeof PRODUCT_CARRIER)[keyof typeof PRODUCT_CARRIER];

export const PRODUCT_CARRIER_META: Record<ProductCarrier, EnumMeta<ProductCarrier>> = {
  CHINA_TELECOM: { label: '中国电信', code: PRODUCT_CARRIER.CHINA_TELECOM },
  CHINA_MOBILE: { label: '中国移动', code: PRODUCT_CARRIER.CHINA_MOBILE },
  CHINA_UNICOM: { label: '中国联通', code: PRODUCT_CARRIER.CHINA_UNICOM },
  CHINA_BROADNET: { label: '中国广电', code: PRODUCT_CARRIER.CHINA_BROADNET },
};

/** 下拉选项的唯一出口（顺序＝上表声明序，与后端枚举声明序同形） */
export const PRODUCT_CARRIER_OPTIONS: readonly { value: ProductCarrier; label: string }[] = (
  Object.values(PRODUCT_CARRIER) as ProductCarrier[]
).map((value) => ({ value, label: PRODUCT_CARRIER_META[value].label }));

/**
 * 迁移期兼容（只此一层，只用于把老数据说成人话）：历史三值 ⇒ 四值。
 * 真库与老导出里还躺着 TELECOM/UNICOM/MOBILE，读面遇到要认得出来、说得成中文。
 * ⛔ 不用于提交、不用于新数据、⛔ 别再把它当第二套枚举出口。
 */
const LEGACY_ORDER_CARRIER: Record<string, ProductCarrier> = {
  TELECOM: PRODUCT_CARRIER.CHINA_TELECOM,
  UNICOM: PRODUCT_CARRIER.CHINA_UNICOM,
  MOBILE: PRODUCT_CARRIER.CHINA_MOBILE,
};

/** 读面的唯一出口：新码/历史码 ⇒ 中文名；认不出的原样回显（⛔ 吞成空串更糟）。 */
export function carrierLabelOf(value?: string | null): string {
  if (!value) return '';
  const key = LEGACY_ORDER_CARRIER[value] ?? value;
  const meta = (PRODUCT_CARRIER_META as Record<string, EnumMeta<ProductCarrier> | undefined>)[key];
  return meta ? meta.label : value;
}

/** 旧类型名的兼容别名：core/types/models.ts 仍按 `Carrier` 引用，值域已是四值。 */
export type Carrier = ProductCarrier;

export const ORDER_EVENT_TYPE = {
  CREATED: 'CREATED',
  /* 紧急需求20261029 需求1：交付端提交「开卡失败」时后端落的订单事件（OrderEventType.CARD_FAILED）。 */
  CARD_FAILED: 'CARD_FAILED',
  VALIDATED: 'VALIDATED',
  ASSIGNED: 'ASSIGNED',
  TO_POOL: 'TO_POOL',
  GRABBED: 'GRABBED',
  RECALLED: 'RECALLED',
  ACCEPTED: 'ACCEPTED',
  CALL_CONTACT: 'CALL_CONTACT',
  DELIVER_START: 'DELIVER_START',
  DELIVERED: 'DELIVERED',
  PROOF_UPLOADED: 'PROOF_UPLOADED',
  CARRIER_CALLBACK: 'CARRIER_CALLBACK',
  ACTIVATED: 'ACTIVATED',
  FIRST_RECHARGE: 'FIRST_RECHARGE',
  RETENTION_CHECK: 'RETENTION_CHECK',
  URGED: 'URGED',
  REASSIGNED: 'REASSIGNED',
  EXCEPTION: 'EXCEPTION',
  SECOND_FOLLOW: 'SECOND_FOLLOW',
  CANCELLED: 'CANCELLED',
} as const;
export type OrderEventType = (typeof ORDER_EVENT_TYPE)[keyof typeof ORDER_EVENT_TYPE];

export const ORDER_EVENT_META: Record<OrderEventType, EnumMeta<OrderEventType>> = {
  CREATED: { label: '订单录入' },
  VALIDATED: { label: '校验通过' },
  ASSIGNED: { label: '分配' },
  TO_POOL: { label: '放入公区' },
  GRABBED: { label: '抢单' },
  RECALLED: { label: '回收' },
  ACCEPTED: { label: '接单' },
  CALL_CONTACT: { label: '电话联系' },
  DELIVER_START: { label: '开始交付' },
  DELIVERED: { label: '交付完成' },
  PROOF_UPLOADED: { label: '凭证上传' },
  CARRIER_CALLBACK: { label: '运营商回调' },
  ACTIVATED: { label: '激活' },
  FIRST_RECHARGE: { label: '首充' },
  RETENTION_CHECK: { label: '留存巡检' },
  URGED: { label: '催办' },
  REASSIGNED: { label: '改派' },
  EXCEPTION: { label: '异常' },
  SECOND_FOLLOW: { label: '二次跟进' },
  CANCELLED: { label: '取消' },
  CARD_FAILED: { label: '开卡失败' },
};

export const PROOF_TYPE = {
  CARD_PHOTO: 'CARD_PHOTO',
  ACTIVATION_PAGE: 'ACTIVATION_PAGE',
  SCENE_PHOTO: 'SCENE_PHOTO',
  SIGNATURE: 'SIGNATURE',
} as const;
export type ProofType = (typeof PROOF_TYPE)[keyof typeof PROOF_TYPE];

export const PROOF_TYPE_META: Record<ProofType, EnumMeta<ProofType>> = {
  CARD_PHOTO: { label: '卡品照' },
  ACTIVATION_PAGE: { label: '激活页' },
  SCENE_PHOTO: { label: '现场照' },
  SIGNATURE: { label: '客户签名' },
};

/** 交付完成规则：≥3 类凭证 + 签名 */
export const PROOF_MIN_KINDS = 3;

export const ALERT_LEVEL = { HIGH: 'HIGH', MEDIUM: 'MEDIUM', LOW: 'LOW' } as const;
export type AlertLevel = (typeof ALERT_LEVEL)[keyof typeof ALERT_LEVEL];

export const ALERT_LEVEL_META: Record<AlertLevel, EnumMeta<AlertLevel>> = {
  HIGH: { label: '高', tone: 'dan' },
  MEDIUM: { label: '中', tone: 'warn' },
  LOW: { label: '低', tone: 'pri' },
};

export const ALERT_TICKET_STATUS = {
  PENDING_CLAIM: 'PENDING_CLAIM',
  PROCESSING: 'PROCESSING',
  CLOSED: 'CLOSED',
  ESCALATED: 'ESCALATED',
} as const;
export type AlertTicketStatus = (typeof ALERT_TICKET_STATUS)[keyof typeof ALERT_TICKET_STATUS];

export const ALERT_TICKET_STATUS_META: Record<AlertTicketStatus, EnumMeta<AlertTicketStatus>> = {
  PENDING_CLAIM: { label: '待认领', tone: 'warn' },
  PROCESSING: { label: '处理中', tone: 'pri' },
  CLOSED: { label: '已关闭', tone: 'ok' },
  ESCALATED: { label: '已升级', tone: 'dan' },
};

export const AUDIT_ACTION = {
  LOGIN: 'LOGIN',
  LOGOUT: 'LOGOUT',
  ORDER_CREATE: 'ORDER_CREATE',
  ORDER_ASSIGN: 'ORDER_ASSIGN',
  ORDER_URGE: 'ORDER_URGE',
  ORDER_CANCEL: 'ORDER_CANCEL',
  EXPORT_REQUEST: 'EXPORT_REQUEST',
  EXPORT_APPROVED: 'EXPORT_APPROVED',
  ROLE_PERM_CHANGE: 'ROLE_PERM_CHANGE',
  RULE_CHANGE: 'RULE_CHANGE',
  CONFIG_CHANGE: 'CONFIG_CHANGE',
  COURIER_AUDIT: 'COURIER_AUDIT',
} as const;
export type AuditAction = (typeof AUDIT_ACTION)[keyof typeof AUDIT_ACTION];

export const AUDIT_ACTION_META: Record<AuditAction, EnumMeta<AuditAction>> = {
  LOGIN: { label: '登录' },
  LOGOUT: { label: '登出' },
  ORDER_CREATE: { label: '订单录入' },
  ORDER_ASSIGN: { label: '订单分配' },
  ORDER_URGE: { label: '订单催办' },
  ORDER_CANCEL: { label: '订单取消' },
  EXPORT_REQUEST: { label: '导出申请' },
  EXPORT_APPROVED: { label: '导出审批' },
  ROLE_PERM_CHANGE: { label: '权限变更' },
  RULE_CHANGE: { label: '规则变更' },
  CONFIG_CHANGE: { label: '配置变更' },
  COURIER_AUDIT: { label: '专员审核' },
};

/** 历史名称映射，仅兼容旧码；不是受理范围或授权区域白名单。 */
export const REGION_CODES = ['WUHOU', 'GAOXIN', 'JINJIANG', 'CHENGHUA', 'SHUANGLIU'] as const;
/** 业务区域码由服务端主数据决定，保留大小写与编码原文。 */
export type RegionCode = string;

export const REGION_META: Record<RegionCode, { label: string }> = {
  WUHOU: { label: '武侯区' },
  GAOXIN: { label: '高新区' },
  JINJIANG: { label: '锦江区' },
  CHENGHUA: { label: '成华区' },
  SHUANGLIU: { label: '双流区' },
};

/** 区域码 → 区名；码不在本端码表里就回空串（⛔ 替它编一个地名），由调用方原样显示码值（seq=1857：不落成破折号，与 dadmin 一致） */
export function regionNameOf(code: string): string {
  return (REGION_META as Record<string, { label: string }>)[code]?.label ?? '';
}

/**
 * 区名显示值＝本端唯一的拼法出口：码表里有名用名，码表外原样带码值（项目负责人 seq=1857），
 * 只有码本身为空才回空串，由调用方落「—」。真区划数据进来之前，⛔ 各视图再各写一份兜底。
 */
export function regionDisplayOf(code: string): string {
  return regionNameOf(code) || code;
}

export function labelOf<T extends string>(
  meta: Record<T, EnumMeta<T>>,
  value: T | string | null | undefined,
  fallback = '—',
): string {
  if (!value) return fallback;
  const hit = meta[value as T];
  return hit ? hit.label : String(value);
}

/**
 * 外部响应枚举值归一：§6.0-③ 约定线上一律 `UPPER_SNAKE_CASE` 明文，但文档描述性写法
 * （如 §6.2-7 `endBinding` 写作 `admin` / `bpo` / `miniapp`）与真实序列化可能大小写不一致。
 * 按大写归一后在**封闭集**内匹配，未命中回落 `fallback` —— 不猜测、不静默转成相近值。
 */
export function normalizeEnum<T extends string>(raw: unknown, allowed: readonly T[], fallback: T): T {
  return normalizeEnumOrNull(raw, allowed) ?? fallback;
}

/** 同上，但未命中返回 `null`（用于可空字段，避免把未知值伪造成合法值） */
export function normalizeEnumOrNull<T extends string>(raw: unknown, allowed: readonly T[]): T | null {
  if (raw === null || raw === undefined) return null;
  const exact = allowed.find((code) => code === raw);
  if (exact) return exact;
  const upper = String(raw).trim().toUpperCase();
  return allowed.find((code) => code === upper) ?? null;
}

export function toneOf<T extends string>(
  meta: Record<T, EnumMeta<T>>,
  value: T | string | null | undefined,
): Tone | undefined {
  if (!value) return undefined;
  return meta[value as T]?.tone;
}

/** 枚举 → 下拉选项 */
export function optionsOf<T extends string>(
  meta: Record<T, EnumMeta<T>>,
): Array<{ value: T; label: string }> {
  return (Object.keys(meta) as T[]).map((key) => ({ value: key, label: meta[key].label }));
}
